/**
 * Create Report View
 *
 * Generates a ReportView from a ReportTemplate and Report.
 * Separates presentation (ViewBlocks) from data (flat values map).
 *
 * This is the bridge between:
 * - Template (structure definitions)
 * - Report (flat data.values)
 * - View (normalized presentation blocks)
 */

import type { DataContext, Report, ReportTemplate, ReportView, ViewSection } from '@/data/schemas';
import { VariableResolver } from '@/data/variableResolver';
import { splitSectionTitle } from './navigationHelpers';
import { createViewSection, expandRepeatingSection } from './reportView/sectionBuilder';
import { createTocBuilder } from './reportView/tocBuilder';

interface CreateReportViewOptions {
  template: ReportTemplate;
  report: Report;
  context?: DataContext; // For live resolution in draft mode
}

/**
 * Creates a ReportView from a template and report.
 *
 * This function transforms the flat report.data.values structure into a hierarchical
 * ViewBlock structure suitable for rendering. It acts as an adapter between the data
 * layer (Report) and the presentation layer (Web/PDF renderers).
 *
 * **What it does**:
 * 1. Maps template sections to ViewSections
 * 2. Expands repeating sections (e.g., refrigeration units)
 * 3. Creates ViewBlocks (kv, table, checklist, grid) from field definitions
 * 4. Applies highlights from report.data.highlights
 * 5. Resolves computed fields if context provided
 *
 * **View Layer Architecture**:
 * ```
 * Report (flat values) + Template
 *          ↓
 *    createReportView()
 *          ↓
 *      ReportView
 *          ↓
 *    ┌─────┴─────┐
 *    ↓           ↓
 *   Web         PDF
 * ```
 *
 * @param options.template - The template defining structure
 * @param options.report - The report containing flat values
 * @param options.context - Optional context for live field resolution (draft mode)
 * @returns A ReportView with structured ViewBlocks ready for rendering
 *
 * @example
 * ```typescript
 * // Basic usage
 * const view = createReportView({
 *   template: await TemplatesRepo.getVersion(report.templateId, report.templateVersion),
 *   report: report,
 * });
 *
 * // With live resolution (draft mode)
 * const view = createReportView({
 *   template,
 *   report,
 *   context: { company, site },
 * });
 *
 * // view.sections = [
 * //   {
 * //     id: "sec_company",
 * //     title: "Company Information",
 * //     blocks: [
 * //       {
 * //         type: "kv",
 * //         items: [
 * //           { label: "Name", value: "Acme Corp", fieldId: "fld_name" },
 * //           { label: "Address", value: "Via Roma 1", fieldId: "fld_address" }
 * //         ]
 * //       }
 * //     ]
 * //   }
 * // ]
 * ```
 */
export function createReportView({
  template,
  report,
  context,
}: CreateReportViewOptions): ReportView {
  const resolver = context ? new VariableResolver(context) : null;
  const locale = template.metadata.locale ?? 'it-IT';
  const applicableSectionIds = report.meta.applicableSections?.length
    ? new Set(report.meta.applicableSections)
    : null;
  const unitIndexByLabel = new Map<string, number>();
  const siteUnits = context?.site?.refrigerationUnits ?? [];

  const sections: ViewSection[] = [];
  const tocBuilder = createTocBuilder(template.groups);

  const normalizeUnitLabel = (value: string): string => value.trim().toLowerCase();

  const resolveUnitGrouping = (
    unitIndex: number | undefined,
    unitLabel?: string
  ): { unitIndex: number | undefined; unitLabel?: string } => {
    const rawLabel = unitLabel?.trim() ?? '';
    const hasLabel = rawLabel.length > 0 && !rawLabel.includes('{{');
    const normalizedLabel = hasLabel ? normalizeUnitLabel(rawLabel) : '';

    let resolvedIndex = unitIndex;

    if (siteUnits.length > 0 && normalizedLabel) {
      const matchedIndex = siteUnits.findIndex((unit) => {
        const nameKey = unit.name ? normalizeUnitLabel(unit.name) : '';
        const idKey = unit.refrigerationUnitId ? normalizeUnitLabel(unit.refrigerationUnitId) : '';
        return nameKey === normalizedLabel || idKey === normalizedLabel;
      });
      if (matchedIndex >= 0) {
        resolvedIndex = matchedIndex;
      }
    }

    if (resolvedIndex === undefined && normalizedLabel) {
      const existing = unitIndexByLabel.get(normalizedLabel);
      if (existing !== undefined) {
        resolvedIndex = existing;
      }
    }

    if (resolvedIndex !== undefined && normalizedLabel) {
      unitIndexByLabel.set(normalizedLabel, resolvedIndex);
    }

    if (resolvedIndex !== undefined && !hasLabel) {
      const unit = siteUnits[resolvedIndex];
      if (unit?.name?.trim()) {
        return { unitIndex: resolvedIndex, unitLabel: unit.name.trim() };
      }
      if (unit?.refrigerationUnitId?.trim()) {
        return {
          unitIndex: resolvedIndex,
          unitLabel: unit.refrigerationUnitId.trim(),
        };
      }
    }

    return {
      unitIndex: resolvedIndex,
      unitLabel: hasLabel ? rawLabel : undefined,
    };
  };

  for (const templateSection of template.sections) {
    if (applicableSectionIds && !applicableSectionIds.has(templateSection.id)) {
      continue;
    }

    const navigationGroup = templateSection.navigationGroup ?? null;

    if (templateSection.type === 'repeating' && templateSection.repeat) {
      const instances = expandRepeatingSection(
        templateSection,
        report,
        resolver,
        locale,
        context ?? null
      );

      instances.forEach(({ viewSection, indices }, position) => {
        sections.push(viewSection);
        if (!navigationGroup) {
          return;
        }

        const labelParts = splitSectionTitle(viewSection.title);
        const baseLabel =
          labelParts.label || splitSectionTitle(templateSection.name).label || templateSection.name;

        const grouping =
          navigationGroup.id === 'centrali'
            ? resolveUnitGrouping(indices[0] ?? position, labelParts.unitLabel)
            : {
                unitIndex: indices[0] ?? position,
                unitLabel: labelParts.unitLabel,
              };

        tocBuilder.addSection({
          navigationGroup,
          sectionId: viewSection.id,
          templateSectionId: templateSection.id,
          label: baseLabel,
          unitIndex: grouping.unitIndex,
          unitLabel: grouping.unitLabel,
        });
      });

      continue;
    }

    const viewSection = createViewSection(
      templateSection,
      report,
      resolver,
      locale,
      context ?? null
    );
    sections.push(viewSection);

    if (!navigationGroup) {
      continue;
    }

    const labelParts = splitSectionTitle(viewSection.title);
    tocBuilder.addSection({
      navigationGroup,
      sectionId: viewSection.id,
      templateSectionId: templateSection.id,
      label: labelParts.label || viewSection.title,
    });
  }

  return {
    sections,
    meta: {
      title: report.meta.title,
      visitDate: report.meta.visitDate,
      inspector: report.meta.inspector,
    },
    toc: tocBuilder.build(),
  };
}
